/* tree.ts — the org chart's tree build and tidy layout, ported verbatim.
 *
 * THE HONESTY RULE, which is the whole feature. There are no reporting lines in the source — the
 * vendor returns one job per person and no manager field — so every edge here is INFERRED from a
 * classified job title and every edge carries its confidence:
 *
 *   solid   exactly one person sits on the nearest higher level in that dept at that site, so that
 *           is their manager unless the company is unusual.
 *   dashed  one of N above them (median N = 3). The BAND is drawn, never a line we cannot support.
 *   apex    nobody above them in that dept at that site.
 *
 * An edge this chart cannot justify is not drawn. That constraint is why the layout has to handle
 * "band" nodes at all, and it is not a detail to simplify away.
 */
import { N, LVLNAME } from '../../lib/fmt.ts';

export interface OrgPerson {
  person_id: number; full_name: string; title: string | null; dept: string | null;
  level: number; level_adj_why: string | null; icp_role: string | null;
  email: string | null; phone: string | null; switchboard?: number | null;
}
export interface Band {
  level: number; name: string; members: OrgPerson[]; shown: number;
  headcount: number; reachable: number;
  edge: 'apex' | 'solid' | 'dashed'; parent: number | null; parentCandidates?: number;
}
export interface Dept {
  dept: string; bands: Band[]; acting: { person_id: number; basis: string } | null;
  headcount: number; shown: number; topLevel: number;
}
export interface Tree {
  scope: 'acct' | 'fac'; key: string; head: Record<string, unknown>; lens: string;
  apex: { person: OrgPerson; basis: string; candidates: number } | null;
  depts: Dept[];
  stats: { shown: number; headcount: number; solid: number; dashed: number; apexes: number; acting: number };
  smallAccount: boolean; acctBuyers: number; minLevel: number; reachableAll: number;
  sites: { id: number; site: string | null; city: string | null; state: string | null; sqft: number | null; reach: number; committee: number; tier: string | null }[];
}

export interface Node {
  id: string; kind: 'person' | 'dept' | 'band' | 'more'; cls?: string;
  p?: OrgPerson; title?: string; meta?: string; ini?: string;
  edge?: 'apex' | 'solid' | 'dashed' | 'plain';
  acting?: string | null; exp?: string; leaf?: boolean;
  children: Node[]; open: boolean;
  // filled by measure/place
  tw: number; cx: number; y: number; depth: number;
}

/* Two card heights, not one. Every node used to be 76px whether it carried five lines or one, which
   put a person's job title hard against their email on the full cards and left a department card as
   a mostly-empty box. "If something's not useful it shouldn't be there" applies to whitespace too.
     PERSON  name / title (up to 2 lines) / email / phone, with room to breathe
     GROUP   the company, a department, a band, a "+12 more" — a label and a count, nothing else */
export const NODE_W = 210, NODE_H = 88, NODE_H_G = 58, GAP_X = 14, ROW_H = 132;
const BAND_CAP = 6, DEPT_CAP = 8;
export const nodeH = (n: Node) => (n && n.p ? NODE_H : NODE_H_G);

/* First name in full, last initial: "Jim F." not "J.F.". A rep cannot ring a switchboard and ask
   for "S.O.", and at a plant with no email on the card the initials were the only identifier on
   screen. Long first names clip so the card geometry is unchanged. */
export const initials = (n: string | null | undefined) => {
  const parts = String(n || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0].length > 11 ? parts[0].slice(0, 10) + '…' : parts[0];
  return parts.length === 1 ? first : `${first} ${parts[parts.length - 1][0].toUpperCase()}.`;
};
export const clipTo = (s: string | null | undefined, n: number) =>
  (String(s || '').length > n ? String(s).slice(0, n - 1) + '…' : String(s || ''));

/* SVG has no text wrapping, so titles wrap on a measured character budget: ~5.85px per character at
   12px semibold across a 158px usable box, two lines, then an ellipsis. */
export function wrapTitle(s: string | null | undefined, perLine = 26, lines = 2): string[] {
  const words = String(s || '').split(/\s+/).filter(Boolean);
  const out: string[] = []; let cur = '';
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + ' ' + w).length <= perLine) cur += ' ' + w;
    else { out.push(cur); cur = w; if (out.length === lines) break; }
  }
  if (out.length < lines && cur) out.push(cur);
  if (out.length > lines) out.length = lines;
  const used = out.join(' ').length;
  if (used < String(s || '').length) {
    let last = out[out.length - 1] || '';
    if (last.length > perLine - 1) last = last.slice(0, perLine - 1);
    out[out.length - 1] = last + '…';
  }
  return out.length ? out : [''];
}

export interface BuildOpts { expand: Record<string, unknown>; depts: Set<string>; showGhosts: boolean }

export function buildTree(t: Tree, o: BuildOpts): Node {
  let seq = 0;
  const mk = (x: Partial<Node>): Node =>
    ({ id: 'n' + (seq++), children: [], open: true, tw: 0, cx: 0, y: 0, depth: 0, kind: 'dept', ...x } as Node);
  const head = (t.head || {}) as Record<string, unknown>;

  /* The COMPANY root is the COMPANY, not a person. The most senior title on file at a large account
     is frequently a side business or a union office — measured: only 174 of 2,472 level-8 people
     hold a real C-suite title of their employer — so rooting the chart on a person would state
     something the data cannot support. The PLANT root IS a person: a Plant Director is a real,
     checkable apex. */
  let root: Node;
  if (t.scope === 'acct') {
    root = mk({ kind: 'dept', cls: 'apex', title: String(head.name || t.key),
      meta: `${N(head.facilities)} plants · ${N(head.reach)} reachable`, ini: 'CO' });
  } else {
    const a = t.apex && t.apex.person;
    root = a ? mk({ kind: 'person', cls: 'apex', p: a, edge: 'plain' })
      : mk({ kind: 'dept', cls: 'apex', title: String(head.site || 'plant ' + t.key), meta: '', ini: 'PL' });
  }

  /* A plant can carry 15 functions, most of them one or two people. Fanning all of them out at the
     top makes a tree ~2,800px wide, which auto-fits to a zoom where no title is readable — and
     legibility at a high-level view is the requirement. So the significant functions lead and the
     long tail sits behind one expander, ranked by how many people are actually in them. */
  const ranked = t.depts.slice().sort((a, b) => b.shown - a.shown);
  const keep = new Set(ranked.slice(0, DEPT_CAP).map((d) => d.dept));
  const showAllDepts = !!o.expand.__depts || t.depts.length <= DEPT_CAP;
  let depts = showAllDepts ? t.depts : t.depts.filter((d) => keep.has(d.dept));
  // A department filter OVERRIDES the cap entirely — if you asked for Quality you get Quality,
  // whether or not it made the top eight by headcount.
  if (o.depts.size) depts = t.depts.filter((d) => o.depts.has(d.dept));

  for (const dp of depts) {
    const dnode = mk({ kind: 'dept', title: dp.dept, ini: 'FN',
      meta: `${N(dp.shown)}${dp.headcount > dp.shown ? ' of ' + N(dp.headcount) : ''} on file`, edge: 'plain' });
    root.children.push(dnode);
    let parent = dnode;          // where the next band attaches
    for (const b of dp.bands) {
      /* A band whose parent is ambiguous gets its OWN node carrying the "one of N" statement, so the
         uncertainty is a visible object in the chart rather than a missing line. */
      let host = parent;
      if (b.edge === 'dashed') {
        host = mk({ kind: 'band', title: `${b.name} · one of ${b.parentCandidates} above`, ini: 'L' + b.level,
          meta: `${b.shown}${b.headcount > b.shown ? ' of ' + N(b.headcount) : ''}`, edge: 'dashed' });
        parent.children.push(host);
      }
      const shown = o.expand[dp.dept + '|' + b.level] ? b.members : b.members.slice(0, BAND_CAP);
      const nodes = shown.map((p) => mk({
        kind: 'person', p, edge: b.edge === 'dashed' ? 'plain' : b.edge,
        acting: dp.acting && dp.acting.person_id === p.person_id ? dp.acting.basis : null,
      }));
      for (const n of nodes) host.children.push(n);
      const hidden = b.members.length - shown.length;
      if (hidden > 0) host.children.push(mk({ kind: 'more', title: `+ ${N(hidden)} more`, ini: '', edge: 'plain',
        meta: LVLNAME[b.level] || '', exp: dp.dept + '|' + b.level }));
      /* People on the roster we hold no way to contact. Off by default — "show only ones we have
         data for" — and revealed by one toggle that states how many it will add. */
      const ghost = b.headcount - b.shown;
      if (ghost > 0 && o.showGhosts) host.children.push(mk({ kind: 'band', title: `+ ${N(ghost)} on file`, ini: '',
        meta: 'no email or phone', edge: 'dashed', leaf: true }));
      // Next band hangs off the single person when we know who it is, otherwise off this band group.
      parent = (b.edge !== 'dashed' && nodes.length === 1) ? nodes[0] : host;
    }
  }
  /* The tail expander only makes sense when the CAP is what is hiding functions. With a department
     filter on, the user chose what to see and "+10 more functions" is just noise beside it. */
  if (!showAllDepts && !o.depts.size) {
    const rest = t.depts.length - depts.length;
    root.children.push(mk({ kind: 'more', title: `+ ${rest} more function${rest > 1 ? 's' : ''}`, ini: '',
      meta: 'smaller teams here', edge: 'plain', exp: '__depts' }));
  }
  return root;
}

/* Tidy top-down layout: measure each subtree's width bottom-up, then place left-to-right and centre
   every parent over its children. Collapsed branches contribute nothing, which is what keeps a
   333-person plant chart drawable. */
export const kidsOf = (n: Node) => (n.open === false ? [] : n.children);

export function measure(n: Node): number {
  const k = kidsOf(n);
  if (!k.length) return (n.tw = NODE_W);
  let t = 0; for (const c of k) t += measure(c) + GAP_X;
  return (n.tw = Math.max(NODE_W, t - GAP_X));
}

export function place(n: Node, left: number, depth: number) {
  n.depth = depth; n.y = depth * ROW_H;
  const k = kidsOf(n);
  if (!k.length) { n.cx = left + n.tw / 2; return; }
  let inner = -GAP_X; for (const c of k) inner += c.tw + GAP_X;
  let x = left + (n.tw - inner) / 2;
  for (const c of k) { place(c, x, depth + 1); x += c.tw + GAP_X; }
  n.cx = (k[0].cx + k[k.length - 1].cx) / 2;
}

export const walk = (n: Node, f: (n: Node) => void) => { f(n); for (const c of kidsOf(n)) walk(c, f); };
export const walkAll = (n: Node, f: (n: Node) => void) => { f(n); for (const c of n.children) walkAll(c, f); };

/* Expansion is keyed on what the node IS, not on its generated id, so rebuilding the tree after a
   "+N more" click does not silently collapse everything the user had already opened. */
export const keyOf = (n: Node) =>
  n.kind + '|' + (n.p ? 'p' + n.p.person_id : (n.title || '') + '~' + (n.meta || ''));

/* Default: open to depth 1 on a large chart, everything on a small one. A 333-person plant opened
   in full is a wall; a chart opened to one box is a dead end. Anything the user has already toggled
   wins over the default. */
export function restoreOpen(root: Node, openState: Record<string, boolean>) {
  const all: Node[] = [];
  (function collect(n: Node, d: number) { n.depth = d; all.push(n); for (const c of n.children) collect(c, d + 1); })(root, 0);
  const depth = all.length > 40 ? 1 : 99;
  for (const n of all) {
    const st = openState[keyOf(n)];
    n.open = st !== undefined ? st : n.depth < depth;
  }
}

export function layout(root: Node): { nodes: Node[]; edges: [Node, Node][] } {
  measure(root); place(root, 0, 0);
  const nodes: Node[] = [], edges: [Node, Node][] = [];
  walk(root, (n) => { nodes.push(n); for (const c of kidsOf(n)) edges.push([n, c]); });
  return { nodes, edges };
}
